import { Backspace } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Busy } from '../atoms/Busy';
import { Icon } from '../atoms/Icon';
import { cardSurface, pressable } from '../lib/card';
import { cn } from '../lib/cn';
import { uiText } from '../i18n';

export interface PinPadProps {
  length?: number;
  /** Called with the complete PIN. Return false to show the error state and clear. */
  onComplete: (pin: string) => boolean | Promise<boolean>;
  label?: string;
  error?: string;
  /** shown while onComplete runs – "PIN wird geprüft …" by default */
  checkingLabel?: string;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'] as const;

/**
 * Large numeric PIN pad for the wall (standing, no keyboard). Keyboard digits work too.
 * Entered digits are never shown, only filled dots; the count is announced politely.
 */
export function PinPad({
  length = 4,
  onComplete,
  label = 'PIN eingeben',
  error,
  checkingLabel,
}: PinPadProps) {
  const [pin, setPin] = useState('');
  const [failed, setFailed] = useState(false);
  // the server checks the PIN – that takes a moment on a slow wall connection
  const [answered, setAnswered] = useState<string | null>(null);
  const checking = pin.length === length && answered !== pin;

  const press = (key: string) => {
    setFailed(false);
    setAnswered(null);
    if (key === 'del') return setPin((p) => p.slice(0, -1));
    setPin((p) => (p.length < length ? p + key : p));
  };

  useEffect(() => {
    if (pin.length !== length) return;
    let active = true;
    void Promise.resolve(onComplete(pin)).then((ok) => {
      if (active) setAnswered(pin);
      if (active && !ok) {
        setFailed(true);
        setPin('');
      }
    });
    return () => {
      active = false;
    };
  }, [pin, length, onComplete]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key);
      if (e.key === 'Backspace') press('del');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // press only uses setters – stable enough for a window listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col items-center gap-gap-xl">
      <div className="flex flex-col items-center gap-gap-md">
        <p className="text-md font-semibold">{label}</p>
        <div className={cn('flex gap-gap-md', failed && 'animate-shake')} aria-hidden>
          {Array.from({ length }, (_, i) => (
            <span
              key={i}
              className={cn(
                'size-icon-sm rounded-avatar border-2 border-text-primary transition',
                i < pin.length && 'bg-text-primary',
              )}
            />
          ))}
        </div>
        {checking ? (
          <Busy label={checkingLabel ?? uiText().pinChecking} className="min-h-touch-min text-sm" />
        ) : (
          <p role="status" className="min-h-touch-min text-sm text-feedback-danger">
            {failed
              ? (error ?? uiText().pinWrong)
              : uiText().pinDigits(String(pin.length), String(length))}
          </p>
        )}
      </div>
      <div className="grid grid-cols-3 gap-gap-md">
        {KEYS.map((key, i) =>
          key === '' ? (
            <span key={`gap-${i}`} />
          ) : (
            <AriaButton
              key={key}
              onPress={() => press(key)}
              aria-label={key === 'del' ? uiText().pinDelete : key}
              className={cn(
                'grid size-touch-target place-items-center rounded-control font-display text-2xl font-black',
                key === 'del' ? 'bg-transparent' : cardSurface,
                pressable,
                'outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
              )}
            >
              {key === 'del' ? <Icon icon={Backspace} size="lg" /> : key}
            </AriaButton>
          ),
        )}
      </div>
    </div>
  );
}
